// Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, css, Global } from '@emotion/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

/* ════════════════════════════════════════════════════════════════════
   DESIGN TOKENS — shared with HomePage & Register
   ════════════════════════════════════════════════════════════════════ */
const t = {
  c: {
    primary: '#0001FC',
    primaryDeep: '#0000B8',
    primaryLight: '#3A3AFF',
    primarySoft: '#E8E8FF',
    primaryFaint: '#F4F4FF',
    gradStart: '#0001FC',
    gradMid:   '#2828D8',
    gradEnd:   '#5050E0',
    ink: '#0F1729',
    inkSoft: '#1E2A47',
    text: '#1A2332',
    textMuted: '#5C6478',
    textLight: '#8C92A3',
    border: '#E5E8EE',
    borderSoft: '#EEF0F4',
    bg: '#FAFAFB',
    bgAlt: '#F4F6FA',
    surface: '#FFFFFF',
    accent: '#FFB547',
    accentLight: '#A5A5FF',
    success: '#1FA67F',
    successSoft: '#E0F5EC',
    danger: '#D0523E',
    dangerSoft: '#FBE7E2',
  },
  shadow: {
    xs: '0 1px 2px rgba(15, 23, 41, 0.04)',
    sm: '0 2px 8px rgba(15, 23, 41, 0.06)',
    md: '0 8px 24px rgba(15, 23, 41, 0.08)',
    lg: '0 18px 48px rgba(15, 23, 41, 0.10)',
    xl: '0 32px 80px rgba(15, 23, 41, 0.14)',
    blueGlow: '0 16px 40px -16px rgba(0, 1, 252, 0.45)',
  },
  radius: {
    xs: '4px', sm: '8px', md: '10px', lg: '14px', xl: '18px', pill: '999px',
  },
  ease: {
    out: 'cubic-bezier(0.22, 1, 0.36, 1)',
    inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  bp: { xs: '380px', sm: '600px', md: '900px', lg: '1100px' },
};

/* ════════════════════════════════════════════════════════════════════
   GLOBAL STYLES
   ════════════════════════════════════════════════════════════════════ */
const globalStyles = css`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; }
  html {
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
  }
  body {
    margin: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: ${t.c.text};
    background: ${t.c.bg};
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
  }
  *::selection { background: ${t.c.primary}; color: #fff; }
  input, textarea, select { font-size: 16px; }
`;

/* ════════════════════════════════════════════════════════════════════
   ANIMATIONS
   ════════════════════════════════════════════════════════════════════ */
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const slideLeft = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const ripple = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.6); opacity: 0; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
const pulseRing = keyframes`
  0%, 100% { transform: scale(1);    opacity: 0.45; }
  50%      { transform: scale(1.12); opacity: 0.65; }
`;
const pulseRingDelay = keyframes`
  0%, 100% { transform: scale(1);    opacity: 0.30; }
  50%      { transform: scale(1.18); opacity: 0.50; }
`;
const drift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(-10px, 14px) scale(1.04); }
`;

/* ════════════════════════════════════════════════════════════════════
   ICONS
   ════════════════════════════════════════════════════════════════════ */
const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M3.5 7 L12 13 L20.5 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 10 V7 a4 4 0 0 1 8 0 V10" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const IconEye = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 12 C5 6 8.5 4 12 4 c3.5 0 7 2 10 8 -3 6 -6.5 8 -10 8 -3.5 0 -7 -2 -10 -8z"
      stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const IconEyeOff = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 12 C5 6 8.5 4 12 4 c3.5 0 7 2 10 8 -3 6 -6.5 8 -10 8 -3.5 0 -7 -2 -10 -8z"
      stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" opacity="0.55" />
    <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.7" opacity="0.55" />
    <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <polyline points="12 5 19 12 12 19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);
const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill={t.c.success} opacity="0.18" />
    <polyline points="7 12.5 10.5 16 17 9"
      stroke={t.c.success} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);
const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill={t.c.danger} opacity="0.18" />
    <line x1="12" y1="8" x2="12" y2="13" stroke={t.c.danger} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1.2" fill={t.c.danger} />
  </svg>
);
const IconLoader = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    style={{ animation: `${spin} 0.8s linear infinite` }} aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeDasharray="26 54" opacity="0.9" />
  </svg>
);
const IconShieldSmall = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2 L20 5 V12 c0 5 -4 8 -8 10 c-4 -2 -8 -5 -8 -10 V5 Z"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none" />
    <polyline points="9 12 11 14 15 10"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);
const IconSparkle = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2 L14 9 L21 11 L14 13 L12 20 L10 13 L3 11 L10 9 Z" />
  </svg>
);

/* Brand panel feature icons (white, matching HomePage icon style) */
const FBrain = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5a3 3 0 1 0-5.997.142 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 19v-7" />
    <path d="M12 5a3 3 0 1 1 5.997.142 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 19" />
  </svg>
);
const FShield = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const FChart = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const FGlobe = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

/* ════════════════════════════════════════════════════════════════════
   PAGE SHELL
   ════════════════════════════════════════════════════════════════════ */
const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  background: ${t.c.bg};
  position: relative;
  overflow-x: hidden;
`;

/* ──────── Logo mark ──────── */
const LogoFrame = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.sm};
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 1, 252, 0.18);
  flex-shrink: 0;
  width: ${p => p.$size || 40}px;
  height: ${p => p.$size || 40}px;
  padding: 4px;

  img {
    width: 100%; height: 100%;
    object-fit: contain;
    display: block;
  }
`;
const LogoMark = ({ size = 40 }) => (
  <LogoFrame $size={size}>
    <img src="/logo192.png" alt="Visioning Assessment" />
  </LogoFrame>
);

/* ════════════════════════════════════════════════════════════════════
   LEFT BRAND PANEL — electric blue gradient, hero-style
   ════════════════════════════════════════════════════════════════════ */
const BrandSide = styled.aside`
  display: none;
  flex: 1;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, ${t.c.gradStart} 0%, ${t.c.gradMid} 55%, ${t.c.gradEnd} 100%);
  color: #fff;
  z-index: 1;

  @media (min-width: ${t.bp.lg}) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 48px 56px;
  }
`;

const BrandPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
`;

const BrandGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle 500px at 85% 10%, rgba(255, 255, 255, 0.18), transparent),
    radial-gradient(circle 400px at 0% 100%, rgba(0, 0, 100, 0.3), transparent);
`;

const BrandOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;

  &.a {
    width: 360px; height: 360px;
    background: rgba(255, 255, 255, 0.2);
    top: 8%; right: -80px;
    animation: ${drift} 14s ${t.ease.inOut} infinite;
  }
  &.b {
    width: 280px; height: 280px;
    background: ${t.c.accentLight};
    opacity: 0.25;
    bottom: -60px; left: 10%;
    animation: ${drift} 18s ${t.ease.inOut} infinite reverse;
  }
`;

const BrandTop = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 14px;
  animation: ${slideRight} 700ms ${t.ease.out} both;

  .wordmark {
    line-height: 1.1;
    .name {
      font-weight: 700;
      font-size: 1.05rem;
      letter-spacing: -0.01em;
      color: #fff;
    }
    .sub {
      display: block;
      font-size: 0.68rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.72);
      font-weight: 500;
      margin-top: 3px;
    }
  }
`;

const BrandMiddle = styled.div`
  position: relative;
  z-index: 2;
  max-width: 520px;
  animation: ${slideRight} 800ms ${t.ease.out} 100ms both;
`;

const BrandBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: ${t.radius.pill};
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  margin-bottom: 28px;

  .dot { width: 6px; height: 6px; border-radius: 50%; background: ${t.c.accent}; flex-shrink: 0; }
`;

const BrandHeadline = styled.h1`
  font-weight: 800;
  font-size: clamp(2.2rem, 3.2vw, 3.2rem);
  line-height: 1.08;
  letter-spacing: -0.025em;
  margin: 0 0 22px;
  color: #fff;

  .accent { color: ${t.c.accentLight}; }
`;

const BrandLede = styled.p`
  font-size: 1.02rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.88);
  margin: 0 0 44px;
  max-width: 460px;
  font-weight: 400;
`;

const BrandFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BrandFeature = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  animation: ${riseIn} 600ms ${t.ease.out} ${p => p.$delay} both;

  .icon {
    flex-shrink: 0;
    width: 44px; height: 44px;
    border-radius: ${t.radius.md};
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: grid;
    place-items: center;
    color: #fff;
    backdrop-filter: blur(8px);
  }

  .text { flex: 1; padding-top: 2px; }

  h4 {
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: -0.01em;
    color: #fff;
    margin: 0 0 4px;
  }
  p {
    font-size: 0.88rem;
    color: rgba(255, 255, 255, 0.78);
    line-height: 1.55;
    margin: 0;
  }
`;

const BrandBottom = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
  animation: ${riseIn} 600ms ${t.ease.out} 600ms both;

  .trust {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.85);
  }
`;

/* ════════════════════════════════════════════════════════════════════
   RIGHT FORM SIDE
   ════════════════════════════════════════════════════════════════════ */
const FormSide = styled.section`
  flex: 1;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
  background: ${t.c.bg};

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 50% 35% at 85% 0%, ${t.c.primarySoft}, transparent 65%),
      radial-gradient(ellipse 40% 35% at 0% 100%, ${t.c.primaryFaint}, transparent 70%);
    pointer-events: none;
    z-index: 0;

    @media (min-width: ${t.bp.lg}) {
      position: absolute;
    }
  }
`;

/* Mobile top bar */
const MobileBar = styled.div`
  display: none;
  @media (max-width: ${t.bp.lg}) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
    background: rgba(250, 250, 251, 0.92);
    backdrop-filter: blur(14px) saturate(140%);
    -webkit-backdrop-filter: blur(14px) saturate(140%);
    border-bottom: 1px solid ${t.c.border};
    position: sticky;
    top: 0;
    z-index: 30;
  }
  @media (max-width: ${t.bp.sm}) { padding: 12px 16px; }
`;

const MobileBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .txt {
    line-height: 1.1;
    .name {
      font-weight: 700;
      font-size: 0.92rem;
      color: ${t.c.ink};
    }
    .sub {
      display: block;
      font-size: 0.62rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: ${t.c.textMuted};
      margin-top: 2px;
      font-weight: 500;
    }
  }

  @media (max-width: ${t.bp.xs}) {
    .txt .sub { display: none; }
  }
`;

const MobilePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${t.c.successSoft};
  border: 1px solid ${t.c.success}30;
  border-radius: ${t.radius.pill};
  font-size: 0.68rem;
  font-weight: 600;
  color: ${t.c.success};

  .dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${t.c.success};
    position: relative;
    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      background: ${t.c.success};
      animation: ${ripple} 2s ${t.ease.out} infinite;
    }
  }

  @media (max-width: ${t.bp.xs}) {
    padding: 5px 10px;
    font-size: 0.65rem;
  }
`;

const FormWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 28px;
  position: relative;
  z-index: 1;

  @media (max-width: ${t.bp.md}) {
    padding: 28px 18px 48px;
    align-items: flex-start;
  }

  @media (max-width: ${t.bp.sm}) {
    padding: 20px 14px 40px;
  }
`;

const FormInner = styled.div`
  width: 100%;
  max-width: 460px;
  animation: ${slideLeft} 700ms ${t.ease.out} both;

  @media (max-width: ${t.bp.lg}) {
    animation: ${riseIn} 600ms ${t.ease.out} both;
  }
`;

/* Intro above the card */
const FormLede = styled.div`
  text-align: center;
  margin-bottom: 24px;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: ${t.c.surface};
    border: 1px solid ${t.c.border};
    border-radius: ${t.radius.pill};
    font-size: 0.72rem;
    font-weight: 600;
    color: ${t.c.primary};
    box-shadow: ${t.shadow.xs};
    margin-bottom: 18px;
    letter-spacing: 0.06em;
    text-transform: uppercase;

    .dot {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: ${t.c.primary};
    }
  }

  h1 {
    font-weight: 800;
    font-size: clamp(1.6rem, 4.2vw, 2.2rem);
    letter-spacing: -0.025em;
    line-height: 1.15;
    color: ${t.c.ink};
    margin: 0 0 10px;

    .accent { color: ${t.c.primary}; }
  }

  p {
    font-size: 0.95rem;
    color: ${t.c.textMuted};
    margin: 0;
    line-height: 1.55;
  }

  @media (max-width: ${t.bp.sm}) {
    margin-bottom: 20px;
    .eyebrow { font-size: 0.68rem; padding: 5px 12px; }
  }
`;

/* The form card */
const Card = styled.div`
  background: ${t.c.surface};
  border-radius: ${t.radius.xl};
  padding: 36px;
  border: 1px solid ${t.c.border};
  box-shadow: ${t.shadow.lg};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${t.c.primary}, ${t.c.primaryLight});
  }

  @media (max-width: ${t.bp.md}) {
    padding: 28px 22px;
    border-radius: ${t.radius.lg};
  }

  @media (max-width: ${t.bp.sm}) {
    padding: 22px 18px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${riseIn} 500ms ${t.ease.out} ${p => p.$delay || '0ms'} both;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 0.84rem;
  font-weight: 600;
  color: ${t.c.text};
  letter-spacing: -0.005em;
`;

const ForgotLink = styled(Link)`
  font-size: 0.78rem;
  color: ${t.c.primary};
  text-decoration: none;
  font-weight: 600;
  padding: 2px 0;
  transition: color 180ms ${t.ease.out};

  &:hover { color: ${t.c.primaryDeep}; text-decoration: underline; }
`;

const InputShell = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:focus-within .field-icon { color: ${t.c.primary}; }
`;

const FieldIcon = styled.span`
  position: absolute;
  left: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${t.c.textLight};
  transition: color 220ms ${t.ease.out};
  pointer-events: none;
  z-index: 1;
`;

const FieldInput = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 ${p => p.$hasToggle ? '52px' : '16px'} 0 46px;
  background: ${t.c.surface};
  border: 1.5px solid ${t.c.border};
  border-radius: ${t.radius.md};
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  color: ${t.c.ink};
  outline: none;
  transition: all 180ms ${t.ease.out};
  box-sizing: border-box;
  -webkit-appearance: none;

  &::placeholder { color: ${t.c.textLight}; font-weight: 400; }

  &:hover:not(:focus):not(:disabled) { border-color: ${t.c.textLight}; }

  &:focus {
    border-color: ${t.c.primary};
    box-shadow: 0 0 0 4px ${t.c.primarySoft};
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px ${t.c.surface} inset;
    -webkit-text-fill-color: ${t.c.ink};
  }

  @media (max-width: ${t.bp.sm}) { height: 50px; }
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  width: 40px; height: 40px;
  border: none;
  background: transparent;
  color: ${t.c.textMuted};
  border-radius: ${t.radius.sm};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 180ms ${t.ease.out};
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    background: ${t.c.bgAlt};
    color: ${t.c.primary};
  }
  &:active:not(:disabled) { background: ${t.c.borderSoft}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RememberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -4px;
`;

const Remember = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  color: ${t.c.text};
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  padding: 8px 0;
  min-height: 36px;

  input {
    appearance: none;
    -webkit-appearance: none;
    width: 18px; height: 18px;
    border: 1.5px solid ${t.c.border};
    border-radius: 5px;
    background: ${t.c.surface};
    cursor: pointer;
    position: relative;
    transition: all 180ms ${t.ease.out};
    margin: 0;
    flex-shrink: 0;

    &:hover { border-color: ${t.c.primary}; }

    &:checked {
      background: ${t.c.primary};
      border-color: ${t.c.primary};
    }
    &:checked::after {
      content: '';
      position: absolute;
      left: 5px; top: 1.5px;
      width: 5px; height: 9px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;

const Submit = styled.button`
  height: 54px;
  margin-top: 8px;
  background: ${t.c.primary};
  color: #fff;
  border: 1.5px solid ${t.c.primary};
  border-radius: ${t.radius.md};
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  overflow: hidden;
  transition: all 220ms ${t.ease.out};
  -webkit-tap-highlight-color: transparent;
  animation: ${riseIn} 500ms ${t.ease.out} 300ms both;

  & > * { position: relative; z-index: 1; }

  &:hover:not(:disabled) {
    background: ${t.c.primaryDeep};
    border-color: ${t.c.primaryDeep};
    transform: translateY(-2px);
    box-shadow: ${t.shadow.blueGlow};
  }

  &:active:not(:disabled) { transform: translateY(0); }

  &:disabled { cursor: not-allowed; opacity: 0.7; }

  ${p => p.$loading && css`
    background: linear-gradient(90deg, ${t.c.primary}, ${t.c.primaryLight}, ${t.c.primary});
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s linear infinite, ${riseIn} 500ms ${t.ease.out} 300ms both;
  `}

  @media (max-width: ${t.bp.sm}) {
    height: 52px;
    font-size: 0.94rem;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 22px 0 8px;
  animation: ${riseIn} 500ms ${t.ease.out} 400ms both;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${t.c.border};
  }

  span {
    font-size: 0.72rem;
    font-weight: 700;
    color: ${t.c.textMuted};
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
`;

const SignupFooter = styled.div`
  text-align: center;
  animation: ${riseIn} 500ms ${t.ease.out} 500ms both;

  p {
    font-size: 0.92rem;
    color: ${t.c.textMuted};
    margin: 0;
  }

  a {
    color: ${t.c.primary};
    font-weight: 600;
    text-decoration: none;
    transition: color 180ms;
    &:hover { color: ${t.c.primaryDeep}; text-decoration: underline; }
  }
`;

const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
  border-radius: ${t.radius.md};
  animation: ${riseIn} 300ms ${t.ease.out} both;

  ${p => p.$type === 'error' && css`
    background: ${t.c.dangerSoft};
    border: 1px solid ${t.c.danger}30;
    border-left: 3px solid ${t.c.danger};
  `}
  ${p => p.$type === 'success' && css`
    background: ${t.c.successSoft};
    border: 1px solid ${t.c.success}30;
    border-left: 3px solid ${t.c.success};
  `}
  ${p => p.$type === 'info' && css`
    background: ${t.c.primaryFaint};
    border: 1px solid ${t.c.primary}25;
    border-left: 3px solid ${t.c.primary};
  `}

  .body { flex: 1; min-width: 0; }
  .title {
    font-size: 0.88rem;
    font-weight: 700;
    color: ${p => p.$type === 'error' ? t.c.danger : p.$type === 'info' ? t.c.primary : t.c.success};
    margin-bottom: 2px;
  }
  .msg {
    font-size: 0.82rem;
    color: ${t.c.text};
    line-height: 1.45;
    word-wrap: break-word;
  }
`;

const TrustRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${t.c.border};
  flex-wrap: wrap;
  font-size: 0.76rem;
  color: ${t.c.textMuted};
  font-weight: 500;

  .item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${t.c.primary};
  }

  @media (max-width: ${t.bp.sm}) {
    gap: 12px;
    font-size: 0.72rem;
  }
`;

const BackHome = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: ${t.c.textMuted};
  text-decoration: none;
  padding: 8px 12px;
  border-radius: ${t.radius.sm};
  transition: all 180ms;
  margin-bottom: 18px;
  font-weight: 500;

  svg { transform: rotate(180deg); }

  &:hover {
    color: ${t.c.primary};
    background: ${t.c.primaryFaint};
  }

  @media (max-width: ${t.bp.lg}) { display: none; }
`;

/* ════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════ */
const getLoginErrorMessage = (err) => {
  if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
    return 'Your connection took longer than expected. Please keep your internet on and try again; we now wait longer for slow networks before giving up.';
  }

  if (!err.response && err.request) {
    return 'We could not reach the server. Please check your internet connection and try again; the sign-in request will automatically retry on slow networks.';
  }

  return err.response?.data?.error || 'Invalid email or password. Please try again.';
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const emailRef = useRef(null);

  useEffect(() => {
    // Don't autofocus on touch devices — keyboard pops up jarringly
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (!isTouch) {
      const timer = setTimeout(() => emailRef.current?.focus(), 420);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    const slowLoginTimer = setTimeout(() => {
      setStatus({
        type: 'info',
        title: 'Still signing you in',
        message: 'This can take a little longer on slower networks. Please keep this page open while we retry securely.',
      });
    }, 12000);

    try {
      await login(email, password);
      setStatus({
        type: 'success',
        title: 'Welcome back',
        message: 'Redirecting to your dashboard…',
      });
      setTimeout(() => navigate('/dashboard'), 800);
    } catch (err) {
      const msg = getLoginErrorMessage(err);
      setStatus({ type: 'error', title: 'Sign in failed', message: msg });
      toast.error(msg);
    } finally {
      clearTimeout(slowLoginTimer);
      setLoading(false);
    }
  };

  return (
    <Page>
      <Global styles={globalStyles} />

      {/* ─────────── LEFT: Brand panel (desktop only) ─────────── */}
      <BrandSide>
        <BrandPattern />
        <BrandGlow />
        <BrandOrb className="a" />
        <BrandOrb className="b" />

        <BrandTop>
          <LogoMark size={46} />
          <div className="wordmark">
            <span className="name">Visioning Assessment</span>
            <span className="sub">Practitioner Portal</span>
          </div>
        </BrandTop>

        <BrandMiddle>
          <BrandBadge>
            <span className="dot" />
            <IconSparkle />
            AI Powered Tool
          </BrandBadge>

          <BrandHeadline>
            Professional tools for your<br />
            <span className="accent">clinical practice</span>.
          </BrandHeadline>

          <BrandLede>
            Sign in to deliver validated assessments, generate instant reports,
            and care for the clients who count on you.
          </BrandLede>

          <BrandFeatures>
            <BrandFeature $delay="250ms">
              <div className="icon"><FShield /></div>
              <div className="text">
                <h4>Secure &amp; compliant</h4>
                <p>Enterprise-grade security with HIPAA-ready controls built in.</p>
              </div>
            </BrandFeature>
            <BrandFeature $delay="350ms">
              <div className="icon"><FBrain /></div>
              <div className="text">
                <h4>Validated instruments</h4>
                <p>A curated library of assessments, clinically framed and scored.</p>
              </div>
            </BrandFeature>
            <BrandFeature $delay="450ms">
              <div className="icon"><FChart /></div>
              <div className="text">
                <h4>Insight-rich reporting</h4>
                <p>Turn responses into clear visual summaries for every session.</p>
              </div>
            </BrandFeature>
          </BrandFeatures>
        </BrandMiddle>

        <BrandBottom>
          <span>© {new Date().getFullYear()} Visioning Assessment</span>
          <span className="trust">
            <IconShieldSmall />
            256-bit SSL encryption
          </span>
        </BrandBottom>
      </BrandSide>

      {/* ─────────── RIGHT: Form panel ─────────── */}
      <FormSide>
        {/* Mobile-only sticky bar */}
        <MobileBar>
          <MobileBrand>
            <LogoMark size={36} />
            <div className="txt">
              <span className="name">Visioning</span>
              <span className="sub">Practitioner Portal</span>
            </div>
          </MobileBrand>
          <MobilePill>
            <span className="dot" />
            Secure
          </MobilePill>
        </MobileBar>

        <FormWrap>
          <FormInner>
            <BackHome to="/">
              <IconArrow />
              <span>Back to home</span>
            </BackHome>

            <FormLede>
              <div className="eyebrow">
                <span className="dot" />
                Practitioner Sign In
              </div>
              <h1>Welcome <span className="accent">back</span>.</h1>
              <p>Enter your credentials to continue to your dashboard.</p>
            </FormLede>

            <Card>
              {status && (
                <AlertBox $type={status.type} role="alert">
                  {status.type === 'error' ? <IconAlert /> : <IconCheck />}
                  <div className="body">
                    <div className="title">{status.title}</div>
                    <div className="msg">{status.message}</div>
                  </div>
                </AlertBox>
              )}

              <Form onSubmit={handleSubmit} noValidate>
                <Field $delay="120ms">
                  <FieldLabel htmlFor="email">Email address</FieldLabel>
                  <InputShell>
                    <FieldIcon className="field-icon"><IconMail /></FieldIcon>
                    <FieldInput
                      ref={emailRef}
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      inputMode="email"
                      disabled={loading}
                      required
                    />
                  </InputShell>
                </Field>

                <Field $delay="200ms">
                  <LabelRow>
                    <FieldLabel htmlFor="password">Password</FieldLabel>
                    <ForgotLink to="/forgot-password">Forgot password?</ForgotLink>
                  </LabelRow>
                  <InputShell>
                    <FieldIcon className="field-icon"><IconLock /></FieldIcon>
                    <FieldInput
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      disabled={loading}
                      required
                      $hasToggle
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={loading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <IconEyeOff /> : <IconEye />}
                    </ToggleBtn>
                  </InputShell>
                </Field>

                <RememberRow>
                  <Remember>
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Keep me signed in</span>
                  </Remember>
                </RememberRow>

                <Submit type="submit" disabled={loading} $loading={loading}>
                  {loading ? (
                    <>
                      <IconLoader />
                      <span>Signing you in…</span>
                    </>
                  ) : (
                    <>
                      <span>Sign in</span>
                      <IconArrow />
                    </>
                  )}
                </Submit>
              </Form>

              <Divider><span>Or</span></Divider>

              <SignupFooter>
                <p>
                  New here? <Link to="/register">Create an account</Link>
                </p>
              </SignupFooter>

              <TrustRow>
                <span className="item">
                  <IconShieldSmall />
                  256-bit SSL
                </span>
                <span className="item">
                  <IconShieldSmall />
                  HIPAA-ready
                </span>
                <span className="item">
                  <IconShieldSmall />
                  Privacy-first
                </span>
              </TrustRow>
            </Card>
          </FormInner>
        </FormWrap>
      </FormSide>
    </Page>
  );
};

export default Login;
